import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
  limit,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { db, auth } from "../../firebase";

import {
  Building2,
  Clock,
  CalendarDays,
  Stethoscope,
  Wrench,
  Users,
  Save,
  Trash2,
  Plus,
  Send,
  Link2,
  Search,
} from "lucide-react";

const TABS = [
  { key: "profile", label: "Profile", icon: Building2 },
  { key: "services", label: "Services", icon: Wrench },
  { key: "doctors", label: "Doctors", icon: Stethoscope },
  { key: "members", label: "Members", icon: Users },
  { key: "bookings", label: "Bookings", icon: CalendarDays },
];

const emptyClinic = {
  clinicName: "",
  publicEmail: "",
  publicPhone: "",
  bio: "",
  addressText: "",
  city: "Tirana",
  country: "Albania",

  slotMinutes: 15,
  autoApprove: false,
  allowOnlineBooking: true,
  whatsapp: "",
  bookingNotice: "Confirm your appointment from MikDok.",
  workingHours: {
    mon: { enabled: true, from: "09:00", to: "17:00" },
    tue: { enabled: true, from: "09:00", to: "17:00" },
    wed: { enabled: true, from: "09:00", to: "17:00" },
    thu: { enabled: true, from: "09:00", to: "17:00" },
    fri: { enabled: true, from: "09:00", to: "17:00" },
    sat: { enabled: false, from: "09:00", to: "14:00" },
    sun: { enabled: false, from: "09:00", to: "14:00" },
  },
};

const dayLabels = {
  mon: "Mon",
  tue: "Tue",
  wed: "Wed",
  thu: "Thu",
  fri: "Fri",
  sat: "Sat",
  sun: "Sun",
};

function uidSafeId(v) {
  return (v || "").trim().replace(/[^\w.-]/g, "_");
}

function reqId(clinicId, doctorId) {
  return `${clinicId}_${doctorId}`;
}

function looksLikeEmail(v) {
  return /@/.test(v || "");
}

export default function ClinicProfileSetup() {
  const [tab, setTab] = useState("profile");
  const [user, setUser] = useState(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const [clinic, setClinic] = useState(emptyClinic);

  // services
  const [services, setServices] = useState([]);
  const [svcDraft, setSvcDraft] = useState({
    name: "",
    price: "",
    currency: "EUR",
    durationMin: 30,
    active: true,
  });

  // clinic doctors attached
  const [clinicDoctors, setClinicDoctors] = useState([]);

  // doctor requests
  const [requests, setRequests] = useState([]);
  const [reqDraft, setReqDraft] = useState({ doctorUid: "", clinicNote: "" });

  // members
  const [members, setMembers] = useState([]);
  const [memberDraft, setMemberDraft] = useState({
    uid: "",
    role: "manager", // manager | staff
    displayName: "",
  });

  // doctor search
  const [doctorSearch, setDoctorSearch] = useState("");
  const [doctorResults, setDoctorResults] = useState([]);
  const [doctorSearching, setDoctorSearching] = useState(false);

  const clinicId = useMemo(() => user?.uid || null, [user]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u || null));
    return () => unsub();
  }, []);

  useEffect(() => {
    let alive = true;

    async function loadAll() {
      if (!clinicId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setMsg("");

      try {
        // clinic doc
        const clinicRef = doc(db, "clinics", clinicId);
        const snap = await getDoc(clinicRef);
        if (alive) setClinic(snap.exists() ? { ...emptyClinic, ...snap.data() } : emptyClinic);

        // services
        const svcRef = collection(db, "clinics", clinicId, "services");
        const svcQ = query(svcRef, orderBy("createdAt", "desc"));
        const svcSnap = await getDocs(svcQ);
        if (alive) setServices(svcSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

        // attached doctors
        const cdRef = collection(db, "clinics", clinicId, "doctors");
        const cdQ = query(cdRef, orderBy("createdAt", "desc"));
        const cdSnap = await getDocs(cdQ);
        if (alive) setClinicDoctors(cdSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

        // members
        try {
          const memRef = collection(db, "clinics", clinicId, "members");
          const memQ = query(memRef, orderBy("createdAt", "desc"));
          const memSnap = await getDocs(memQ);
          if (alive) setMembers(memSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        } catch (e) {
          console.error("Members load denied:", e);
          if (alive) setMembers([]);
        }

        // requests: best-effort (may require index)
      // requests (only for this clinic)
try {
  const mine = await fetchClinicDoctorRequests(clinicId);
  if (alive) setRequests(mine);
} catch (e) {
  console.error("Requests load failed:", e);

  // Fallback if index not created yet (no orderBy)
  try {
    const ref = collection(db, "clinicDoctorRequests");
    const qy2 = query(ref, where("clinicId", "==", clinicId), limit(50));
    const snap2 = await getDocs(qy2);
    const mine2 = snap2.docs.map(d => ({ id: d.id, ...d.data() }));
    if (alive) setRequests(mine2);
  } catch (e2) {
    console.error("Requests fallback failed:", e2);
    if (alive) setRequests([]);
  }
}

      } catch (e) {
        console.error(e);
        if (alive) setMsg("Failed to load clinic setup data.");
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadAll();
    return () => {
      alive = false;
    };
  }, [clinicId]);

  async function saveClinic() {
    if (!clinicId) return;
    setSaving(true);
    setMsg("");

    try {
      const clinicRef = doc(db, "clinics", clinicId);

      if (!clinic.clinicName?.trim()) {
        setMsg("Clinic name is required.");
        setSaving(false);
        return;
      }

      const payload = {
        ...clinic,
        clinicName: clinic.clinicName.trim(),
        publicEmail: (clinic.publicEmail || "").trim(),
        publicPhone: (clinic.publicPhone || "").trim(),
        addressText: (clinic.addressText || "").trim(),
        updatedAt: serverTimestamp(),
        ownerUid: clinicId,
        type: "clinic",
      };

      const existing = await getDoc(clinicRef);
      if (!existing.exists()) payload.createdAt = serverTimestamp();

      await setDoc(clinicRef, payload, { merge: true });
      setMsg("Saved ✅");
    } catch (e) {
      console.error(e);
      setMsg("Save failed.");
    } finally {
      setSaving(false);
    }
  }

  /* =========================
     SERVICES
     ========================= */

  async function addService() {
    if (!clinicId) return;

    const name = (svcDraft.name || "").trim();
    if (!name) return setMsg("Service name is required.");

    const id = uidSafeId(`${name}-${Date.now()}`);
    const ref = doc(db, "clinics", clinicId, "services", id);

    const priceNum = svcDraft.price === "" || svcDraft.price == null ? null : Number(svcDraft.price);

    const payload = {
      name,
      price: Number.isFinite(priceNum) ? priceNum : null,
      currency: svcDraft.currency || "EUR",
      durationMin: Number(svcDraft.durationMin) || 30,
      active: !!svcDraft.active,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    try {
      setMsg("");
      await setDoc(ref, payload, { merge: true });
      setServices((prev) => [{ id, ...payload }, ...prev]);
      setSvcDraft({ name: "", price: "", currency: "EUR", durationMin: 30, active: true });
      setMsg("Service added ✅");
    } catch (e) {
      console.error(e);
      setMsg("Failed to add service.");
    }
  }

  async function deleteService(serviceId) {
    if (!clinicId) return;
    try {
      setMsg("");
      await deleteDoc(doc(db, "clinics", clinicId, "services", serviceId));
      setServices((prev) => prev.filter((x) => x.id !== serviceId));
      setMsg("Service removed ✅");
    } catch (e) {
      console.error(e);
      setMsg("Failed to remove service.");
    }
  }

  /* =========================
     MEMBERS
     ========================= */

  async function addMember() {
    if (!clinicId) return;

    const uid = (memberDraft.uid || "").trim();
    if (!uid) return setMsg("Member UID is required.");
    if (uid === clinicId) return setMsg("Owner is already the clinic account.");

    const id = uidSafeId(uid);
    const ref = doc(db, "clinics", clinicId, "members", id);

    const payload = {
      uid: id,
      role: memberDraft.role || "staff",
      displayName: (memberDraft.displayName || "").trim(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    try {
      setMsg("");
      await setDoc(ref, payload, { merge: true });
      setMembers((prev) => [{ id, ...payload }, ...prev]);
      setMemberDraft({ uid: "", role: "manager", displayName: "" });
      setMsg("Member added ✅");
    } catch (e) {
      console.error(e);
      setMsg("Failed to add member (permissions or invalid UID).");
    }
  }

  async function removeMember(memberUid) {
    if (!clinicId) return;
    try {
      setMsg("");
      await deleteDoc(doc(db, "clinics", clinicId, "members", memberUid));
      setMembers((prev) => prev.filter((x) => x.id !== memberUid));
      setMsg("Member removed ✅");
    } catch (e) {
      console.error(e);
      setMsg("Failed to remove member.");
    }
  }

  /* =========================
     DOCTOR SEARCH + REQUEST + ATTACH
     ========================= */

  async function searchDoctors() {
    if (!clinicId) return;

    const qv = (doctorSearch || "").trim();
    if (!qv) {
      setDoctorResults([]);
      return;
    }

    setDoctorSearching(true);
    setMsg("");

    try {
      // 1) If looks like UID -> try docId lookup
      if (!looksLikeEmail(qv)) {
        const snap = await getDoc(doc(db, "doctors", qv));
        if (snap.exists()) {
          setDoctorResults([{ id: qv, ...snap.data() }]);
        } else {
          setDoctorResults([]);
          setMsg("No doctor found with that UID.");
        }
        return;
      }

      // 2) If email -> query by field (change "email" if your schema uses something else)
      const dRef = collection(db, "doctors");
      const emailQ = query(dRef, where("email", "==", qv.toLowerCase()), limit(10));
      const rs = await getDocs(emailQ);

      const out = rs.docs.map((d) => ({ id: d.id, ...d.data() }));
      setDoctorResults(out);
      if (out.length === 0) setMsg("No doctor found with that email.");
    } catch (e) {
      console.error(e);
      setMsg("Doctor search failed. Check your doctor fields/index.");
      setDoctorResults([]);
    } finally {
      setDoctorSearching(false);
    }
  }

  async function sendDoctorRequest(doctorUid, doctorData) {
    if (!clinicId) return;

    const doctorId = (doctorUid || "").trim();
    if (!doctorId) return setMsg("Doctor UID is required.");

    const id = reqId(clinicId, doctorId);
    const ref = doc(db, "clinicDoctorRequests", id);

    const payload = {
      clinicId,
      doctorId,
      status: "pending",
      clinicNote: (reqDraft.clinicNote || "").trim(),
      doctorDisplayName:
        doctorData?.displayName ||
        doctorData?.fullName ||
        doctorData?.name ||
        "",
      specialty: doctorData?.specialty || "",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    try {
      setMsg("");
      await setDoc(ref, payload, { merge: true });
      setRequests((prev) => [{ id, ...payload }, ...prev]);
      setMsg("Request sent ✅ (Doctor must accept)");
    } catch (e) {
      console.error(e);
      setMsg("Failed to send request. Make sure doctor UID exists in /doctors.");
    }
  }

  async function attachAcceptedDoctor(doctorUid) {
    if (!clinicId) return;

    const doctorId = (doctorUid || "").trim();
    if (!doctorId) return;

    const requestDocId = reqId(clinicId, doctorId);

    try {
      setMsg("");

      const rSnap = await getDoc(doc(db, "clinicDoctorRequests", requestDocId));
      if (!rSnap.exists()) return setMsg("No request found for this doctor.");
      const r = rSnap.data();
      if (r.status !== "accepted") return setMsg("Doctor has not accepted yet.");

      const ref = doc(db, "clinics", clinicId, "doctors", doctorId);
      const payload = {
        clinicId,
        doctorId,
        displayName: r.doctorDisplayName || "",
        specialty: r.specialty || "",
        active: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(ref, payload, { merge: true });
      setClinicDoctors((prev) => [{ id: doctorId, ...payload }, ...prev]);
      setMsg("Doctor attached ✅");
    } catch (e) {
      console.error(e);
      setMsg("Attach failed (permissions or request not accepted).");
    }
  }

async function fetchClinicDoctorRequests(clinicId) {
  const ref = collection(db, "clinicDoctorRequests");

  // Preferred (needs an index: clinicId + createdAt desc)
  const qy = query(
    ref,
    where("clinicId", "==", clinicId),
    orderBy("createdAt", "desc"),
    limit(50)
  );

  const snap = await getDocs(qy);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

  async function removeClinicDoctor(doctorUid) {
    if (!clinicId) return;
    try {
      setMsg("");
      await deleteDoc(doc(db, "clinics", clinicId, "doctors", doctorUid));
      setClinicDoctors((prev) => prev.filter((x) => x.id !== doctorUid));
      setMsg("Doctor removed ✅");
    } catch (e) {
      console.error(e);
      setMsg("Failed to remove doctor.");
    }
  }

  function uiCard(children) {
    return (
      <div
        style={{
          background: "#fff",
          border: "1px solid rgba(20,30,60,.10)",
          borderRadius: 16,
          padding: 16,
          boxShadow: "0 14px 30px rgba(0,0,0,.06)",
        }}
      >
        {children}
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{ padding: 24, maxWidth: 900, margin: "0 auto" }}>
        {uiCard(
          <>
            <h2 style={{ margin: 0 }}>Clinic setup</h2>
            <p style={{ marginTop: 8, color: "rgba(20,30,60,.7)" }}>
              Please login as a clinic to continue.
            </p>
          </>
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 14 }}>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 14,
            display: "grid",
            placeItems: "center",
            border: "1px solid rgba(20,30,60,.10)",
            background: "linear-gradient(135deg, rgba(59,130,246,.15), rgba(99,102,241,.12))",
          }}
        >
          <Building2 />
        </div>
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0, fontSize: 20 }}>Clinic Profile Setup</h1>
          <div style={{ color: "rgba(20,30,60,.65)", fontSize: 13 }}>
            Manage profile, services, doctors, members and booking settings
          </div>
        </div>

        <button onClick={saveClinic} disabled={saving || loading} style={topSaveBtn}>
          <Save size={18} />
          {saving ? "Saving..." : "Save"}
        </button>
      </div>

      {!!msg && <div style={msgStyle}>{msg}</div>}

      {/* Tabs */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                display: "inline-flex",
                gap: 8,
                alignItems: "center",
                padding: "10px 12px",
                borderRadius: 999,
                border: "1px solid rgba(20,30,60,.10)",
                background: active ? "rgba(99,102,241,.10)" : "#fff",
                cursor: "pointer",
                fontWeight: 700,
              }}
            >
              <Icon size={16} />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Body */}
      {loading ? (
        uiCard(<div style={{ padding: 6 }}>Loading…</div>)
      ) : (
        <>
          {/* PROFILE */}
          {tab === "profile" && (
            <div style={{ display: "grid", gridTemplateColumns: "1.1fr .9fr", gap: 14 }}>
              {uiCard(
                <>
                  <h3 style={{ marginTop: 0 }}>Public profile</h3>

                  <div style={{ display: "grid", gap: 10 }}>
                    <label style={{ display: "grid", gap: 6 }}>
                      <span style={labelStyle}>Clinic name</span>
                      <input
                        value={clinic.clinicName}
                        onChange={(e) => setClinic((p) => ({ ...p, clinicName: e.target.value }))}
                        placeholder="e.g. Klinika ABC"
                        style={inputStyle}
                      />
                    </label>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <label style={{ display: "grid", gap: 6 }}>
                        <span style={labelStyle}>Phone</span>
                        <input
                          value={clinic.publicPhone}
                          onChange={(e) => setClinic((p) => ({ ...p, publicPhone: e.target.value }))}
                          placeholder="+355 ..."
                          style={inputStyle}
                        />
                      </label>

                      <label style={{ display: "grid", gap: 6 }}>
                        <span style={labelStyle}>Email</span>
                        <input
                          value={clinic.publicEmail}
                          onChange={(e) => setClinic((p) => ({ ...p, publicEmail: e.target.value }))}
                          placeholder="clinic@email.com"
                          style={inputStyle}
                        />
                      </label>
                    </div>

                    <label style={{ display: "grid", gap: 6 }}>
                      <span style={labelStyle}>Bio</span>
                      <textarea
                        value={clinic.bio}
                        onChange={(e) => setClinic((p) => ({ ...p, bio: e.target.value }))}
                        placeholder="Short description for patients..."
                        style={{ ...inputStyle, minHeight: 110, resize: "vertical", paddingTop: 10 }}
                      />
                    </label>

                    <div style={{ display: "grid", gridTemplateColumns: "1.4fr .6fr", gap: 10 }}>
                      <label style={{ display: "grid", gap: 6 }}>
                        <span style={labelStyle}>Address</span>
                        <input
                          value={clinic.addressText}
                          onChange={(e) => setClinic((p) => ({ ...p, addressText: e.target.value }))}
                          placeholder="Street, area"
                          style={inputStyle}
                        />
                      </label>

                      <label style={{ display: "grid", gap: 6 }}>
                        <span style={labelStyle}>City</span>
                        <input
                          value={clinic.city}
                          onChange={(e) => setClinic((p) => ({ ...p, city: e.target.value }))}
                          style={inputStyle}
                        />
                      </label>
                    </div>
                  </div>
                </>
              )}

              {uiCard(
                <>
                  <h3 style={{ marginTop: 0 }}>Visibility</h3>

                  <label style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
                    <input
                      type="checkbox"
                      checked={!!clinic.allowOnlineBooking}
                      onChange={(e) => setClinic((p) => ({ ...p, allowOnlineBooking: e.target.checked }))}
                    />
                    <span style={{ fontWeight: 700 }}>Allow online booking</span>
                  </label>

                  <div style={{ fontSize: 13, color: "rgba(20,30,60,.7)", lineHeight: 1.5 }}>
                    Patients can request appointments on MikDok when enabled.
                  </div>

                  <div style={{ height: 12 }} />

                  <label style={{ display: "grid", gap: 6 }}>
                    <span style={labelStyle}>WhatsApp (optional)</span>
                    <input
                      value={clinic.whatsapp}
                      onChange={(e) => setClinic((p) => ({ ...p, whatsapp: e.target.value }))}
                      placeholder="e.g. +3556xxxxxxx"
                      style={inputStyle}
                    />
                  </label>
                </>
              )}
            </div>
          )}

          {/* SERVICES */}
          {tab === "services" && (
            <div style={{ display: "grid", gridTemplateColumns: ".9fr 1.1fr", gap: 14 }}>
              {uiCard(
                <>
                  <h3 style={{ marginTop: 0 }}>Add service</h3>

                  <div style={{ display: "grid", gap: 10 }}>
                    <label style={{ display: "grid", gap: 6 }}>
                      <span style={labelStyle}>Service name</span>
                      <input
                        value={svcDraft.name}
                        onChange={(e) => setSvcDraft((p) => ({ ...p, name: e.target.value }))}
                        placeholder="e.g. Dermatology consultation"
                        style={inputStyle}
                      />
                    </label>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <label style={{ display: "grid", gap: 6 }}>
                        <span style={labelStyle}>Price</span>
                        <input
                          value={svcDraft.price}
                          onChange={(e) => setSvcDraft((p) => ({ ...p, price: e.target.value }))}
                          placeholder="e.g. 30"
                          style={inputStyle}
                        />
                      </label>

                      <label style={{ display: "grid", gap: 6 }}>
                        <span style={labelStyle}>Currency</span>
                        <select
                          value={svcDraft.currency}
                          onChange={(e) => setSvcDraft((p) => ({ ...p, currency: e.target.value }))}
                          style={inputStyle}
                        >
                          <option value="EUR">EUR</option>
                          <option value="ALL">ALL</option>
                        </select>
                      </label>
                    </div>

                    <label style={{ display: "grid", gap: 6 }}>
                      <span style={labelStyle}>Duration (minutes)</span>
                      <input
                        type="number"
                        value={svcDraft.durationMin}
                        onChange={(e) => setSvcDraft((p) => ({ ...p, durationMin: e.target.value }))}
                        style={inputStyle}
                      />
                    </label>

                    <label style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      <input
                        type="checkbox"
                        checked={!!svcDraft.active}
                        onChange={(e) => setSvcDraft((p) => ({ ...p, active: e.target.checked }))}
                      />
                      <span style={{ fontWeight: 700 }}>Active</span>
                    </label>

                    <button onClick={addService} style={primaryBtn}>
                      <Plus size={18} /> Add service
                    </button>
                  </div>
                </>
              )}

              {uiCard(
                <>
                  <h3 style={{ marginTop: 0 }}>Your services</h3>

                  {services.length === 0 ? (
                    <div style={{ color: "rgba(20,30,60,.65)" }}>No services yet.</div>
                  ) : (
                    <div style={{ display: "grid", gap: 10 }}>
                      {services.map((s) => (
                        <div key={s.id} style={rowCard}>
                          <div>
                            <div style={{ fontWeight: 800 }}>{s.name}</div>
                            <div style={{ fontSize: 13, color: "rgba(20,30,60,.65)" }}>
                              {s.price != null ? `${s.price} ${s.currency || "EUR"}` : "Price: —"} •{" "}
                              <Clock size={14} style={{ verticalAlign: "-2px" }} /> {s.durationMin || 30} min •{" "}
                              {s.active ? "Active" : "Hidden"}
                            </div>
                          </div>

                          <button onClick={() => deleteService(s.id)} style={dangerBtn} title="Remove">
                            <Trash2 size={18} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* DOCTORS */}
          {tab === "doctors" && (
            <div style={{ display: "grid", gridTemplateColumns: ".95fr 1.05fr", gap: 14 }}>
              {uiCard(
                <>
                  <h3 style={{ marginTop: 0 }}>Search doctor then send request</h3>

                  <div style={{ display: "grid", gap: 10 }}>
                    <label style={{ display: "grid", gap: 6 }}>
                      <span style={labelStyle}>Search by doctor UID or email</span>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 44px", gap: 10 }}>
                        <input
                          value={doctorSearch}
                          onChange={(e) => setDoctorSearch(e.target.value)}
                          placeholder="doctor UID (doc id) or email"
                          style={inputStyle}
                        />
                        <button onClick={searchDoctors} style={softBtn} title="Search">
                          <Search size={18} />
                        </button>
                      </div>
                    </label>

                    {doctorSearching && (
                      <div style={{ fontSize: 13, color: "rgba(20,30,60,.65)" }}>Searching…</div>
                    )}

                    {doctorResults.length > 0 && (
                      <div style={{ display: "grid", gap: 10 }}>
                        {doctorResults.map((d) => (
                          <div key={d.id} style={rowCard}>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: 900 }}>
                                {d.displayName || d.fullName || d.name || d.id}
                              </div>
                              <div style={{ fontSize: 12, color: "rgba(20,30,60,.65)" }}>
                                UID: {d.id} {d.specialty ? `• ${d.specialty}` : ""}
                              </div>
                            </div>

                            <button
                              onClick={() => sendDoctorRequest(d.id, d)}
                              style={primaryBtnSmall}
                              title="Send request"
                            >
                              <Send size={16} /> Request
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <label style={{ display: "grid", gap: 6 }}>
                      <span style={labelStyle}>Clinic note (optional)</span>
                      <input
                        value={reqDraft.clinicNote}
                        onChange={(e) => setReqDraft((p) => ({ ...p, clinicNote: e.target.value }))}
                        placeholder="e.g. We want to list your appointments at our clinic"
                        style={inputStyle}
                      />
                    </label>

                    <div style={{ fontSize: 12, color: "rgba(20,30,60,.65)", lineHeight: 1.5 }}>
                      Doctor must accept the request before you can attach them.
                    </div>
                  </div>

                  <div style={{ height: 14 }} />

                  <h4 style={{ margin: 0 }}>Requests</h4>
                  <div style={{ marginTop: 10, display: "grid", gap: 10 }}>
                    {requests.length === 0 ? (
                      <div style={{ color: "rgba(20,30,60,.65)" }}>No requests loaded (or index not set).</div>
                    ) : (
                      requests.map((r) => (
                        <div key={r.id} style={rowCard}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 800 }}>
                              {r.doctorDisplayName}
                              <span
                                style={{
                                  marginLeft: 8,
                                  fontSize: 12,
                                  padding: "2px 8px",
                                  borderRadius: 999,
                                  border: "1px solid rgba(20,30,60,.12)",
                                  background:
                                    r.status === "accepted"
                                      ? "rgba(16,185,129,.10)"
                                      : r.status === "rejected"
                                      ? "rgba(220,38,38,.08)"
                                      : "rgba(99,102,241,.08)",
                                }}
                              >
                                {r.status}
                              </span>
                              <div style={{ fontWeight: 400, fontSize: 13 }}>
                              {r.specialty}
                              </div>
                             
                            </div>
                            {!!r.clinicNote && (
                              <div style={{ fontSize: 12, color: "rgba(20,30,60,.65)", marginTop: 4 }}>
                                Note: {r.clinicNote}
                              </div>
                            )}
                          </div>

                          <button
                            onClick={() => attachAcceptedDoctor(r.doctorId)}
                            style={{
                              ...softBtn,
                              opacity: r.status === "accepted" ? 1 : 0.5,
                              cursor: r.status === "accepted" ? "pointer" : "not-allowed",
                            }}
                            disabled={r.status !== "accepted"}
                            title="Attach doctor (only if accepted)"
                          >
                            <Link2 size={18} />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}

              {uiCard(
                <>
                  <h3 style={{ marginTop: 0 }}>Clinic doctors (attached)</h3>

                  {clinicDoctors.length === 0 ? (
                    <div style={{ color: "rgba(20,30,60,.65)" }}>No doctors attached yet.</div>
                  ) : (
                    <div style={{ display: "grid", gap: 10 }}>
                      {clinicDoctors.map((d) => (
                        <div key={d.id} style={rowCard}>
                          <div>
                            <div style={{ fontWeight: 800 }}>{d.displayName || d.doctorId}</div>
                            <div style={{ fontSize: 12, color: "rgba(20,30,60,.60)" }}>
                              {d.specialty ? `${d.specialty} • ` : ""}
                              {d.active ? "Active" : "Hidden"} • UID: {d.doctorId}
                            </div>
                          </div>

                          <button onClick={() => removeClinicDoctor(d.id)} style={dangerBtn} title="Remove">
                            <Trash2 size={18} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* MEMBERS */}
          {tab === "members" && (
            <div style={{ display: "grid", gridTemplateColumns: ".9fr 1.1fr", gap: 14 }}>
              {uiCard(
                <>
                  <h3 style={{ marginTop: 0 }}>Add member</h3>

                  <div style={{ display: "grid", gap: 10 }}>
                    <label style={{ display: "grid", gap: 6 }}>
                      <span style={labelStyle}>User UID</span>
                      <input
                        value={memberDraft.uid}
                        onChange={(e) => setMemberDraft((p) => ({ ...p, uid: e.target.value }))}
                        placeholder="Paste the user UID"
                        style={inputStyle}
                      />
                    </label>

                    <label style={{ display: "grid", gap: 6 }}>
                      <span style={labelStyle}>Display name (optional)</span>
                      <input
                        value={memberDraft.displayName}
                        onChange={(e) => setMemberDraft((p) => ({ ...p, displayName: e.target.value }))}
                        placeholder="e.g. Reception Desk"
                        style={inputStyle}
                      />
                    </label>

                    <label style={{ display: "grid", gap: 6 }}>
                      <span style={labelStyle}>Role</span>
                      <select
                        value={memberDraft.role}
                        onChange={(e) => setMemberDraft((p) => ({ ...p, role: e.target.value }))}
                        style={inputStyle}
                      >
                        <option value="manager">manager</option>
                        <option value="staff">staff</option>
                      </select>
                    </label>

                    <button onClick={addMember} style={primaryBtn}>
                      <Plus size={18} /> Add member
                    </button>

                    <div style={{ fontSize: 12, color: "rgba(20,30,60,.65)", lineHeight: 1.5 }}>
                      Owners/managers can add members. Managers cannot create “owner”.
                    </div>
                  </div>
                </>
              )}

              {uiCard(
                <>
                  <h3 style={{ marginTop: 0 }}>Members</h3>

                  {members.length === 0 ? (
                    <div style={{ color: "rgba(20,30,60,.65)" }}>No members yet.</div>
                  ) : (
                    <div style={{ display: "grid", gap: 10 }}>
                      {members.map((m) => (
                        <div key={m.id} style={rowCard}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 800 }}>
                              {m.displayName || m.uid || m.id}
                              <span
                                style={{
                                  marginLeft: 8,
                                  fontSize: 12,
                                  padding: "2px 8px",
                                  borderRadius: 999,
                                  border: "1px solid rgba(20,30,60,.12)",
                                  background: "rgba(99,102,241,.08)",
                                }}
                              >
                                {m.role || "staff"}
                              </span>
                            </div>
                            <div style={{ fontSize: 12, color: "rgba(20,30,60,.55)", marginTop: 4 }}>
                              UID: {m.uid || m.id}
                            </div>
                          </div>

                          <button onClick={() => removeMember(m.id)} style={dangerBtn} title="Remove">
                            <Trash2 size={18} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* BOOKINGS (settings only) */}
          {tab === "bookings" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 14 }}>
              {uiCard(
                <>
                  <h3 style={{ marginTop: 0 }}>Booking settings</h3>

                  <div style={{ display: "grid", gap: 12 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <label style={{ display: "grid", gap: 6 }}>
                        <span style={labelStyle}>Slot duration (minutes)</span>
                        <input
                          type="number"
                          value={clinic.slotMinutes}
                          onChange={(e) =>
                            setClinic((p) => ({ ...p, slotMinutes: Number(e.target.value) || 15 }))
                          }
                          style={inputStyle}
                        />
                      </label>

                      <label style={{ display: "flex", gap: 10, alignItems: "center", paddingTop: 24 }}>
                        <input
                          type="checkbox"
                          checked={!!clinic.autoApprove}
                          onChange={(e) => setClinic((p) => ({ ...p, autoApprove: e.target.checked }))}
                        />
                        <span style={{ fontWeight: 800 }}>Auto-approve bookings</span>
                      </label>
                    </div>

                    <label style={{ display: "grid", gap: 6 }}>
                      <span style={labelStyle}>Booking notice (shown to patient)</span>
                      <input
                        value={clinic.bookingNotice}
                        onChange={(e) => setClinic((p) => ({ ...p, bookingNotice: e.target.value }))}
                        style={inputStyle}
                      />
                    </label>

                    <div style={{ borderTop: "1px solid rgba(20,30,60,.10)", paddingTop: 12 }}>
                      <div style={{ fontWeight: 900, marginBottom: 8 }}>Working hours</div>

                      <div style={{ display: "grid", gap: 10 }}>
                        {Object.keys(clinic.workingHours || {}).map((k) => {
                          const day = clinic.workingHours[k];
                          return (
                            <div
                              key={k}
                              style={{
                                display: "grid",
                                gridTemplateColumns: "120px 90px 1fr 1fr",
                                gap: 10,
                                alignItems: "center",
                                padding: 10,
                                borderRadius: 14,
                                border: "1px solid rgba(20,30,60,.10)",
                              }}
                            >
                              <div style={{ fontWeight: 800 }}>{dayLabels[k] || k}</div>

                              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                <input
                                  type="checkbox"
                                  checked={!!day.enabled}
                                  onChange={(e) =>
                                    setClinic((p) => ({
                                      ...p,
                                      workingHours: {
                                        ...p.workingHours,
                                        [k]: { ...p.workingHours[k], enabled: e.target.checked },
                                      },
                                    }))
                                  }
                                />
                                <span style={{ fontSize: 13 }}>Open</span>
                              </label>

                              <input
                                disabled={!day.enabled}
                                value={day.from}
                                onChange={(e) =>
                                  setClinic((p) => ({
                                    ...p,
                                    workingHours: {
                                      ...p.workingHours,
                                      [k]: { ...p.workingHours[k], from: e.target.value },
                                    },
                                  }))
                                }
                                style={{ ...inputStyle, opacity: day.enabled ? 1 : 0.5 }}
                              />

                              <input
                                disabled={!day.enabled}
                                value={day.to}
                                onChange={(e) =>
                                  setClinic((p) => ({
                                    ...p,
                                    workingHours: {
                                      ...p.workingHours,
                                      [k]: { ...p.workingHours[k], to: e.target.value },
                                    },
                                  }))
                                }
                                style={{ ...inputStyle, opacity: day.enabled ? 1 : 0.5 }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <button onClick={saveClinic} disabled={saving} style={primaryBtn}>
                      <Save size={18} /> Save booking settings
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* =========================
   styles
   ========================= */

const labelStyle = { fontSize: 13, color: "rgba(20,30,60,.7)" };

const inputStyle = {
  height: 42,
  borderRadius: 12,
  border: "1px solid rgba(20,30,60,.12)",
  padding: "0 12px",
  outline: "none",
  fontSize: 14,
  background: "#fff",
};

const primaryBtn = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  padding: "10px 12px",
  borderRadius: 12,
  border: "1px solid rgba(20,30,60,.10)",
  background: "linear-gradient(135deg, rgba(59,130,246,1), rgba(99,102,241,1))",
  color: "#fff",
  fontWeight: 900,
  cursor: "pointer",
};

const primaryBtnSmall = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  padding: "10px 10px",
  borderRadius: 12,
  border: "1px solid rgba(20,30,60,.10)",
  background: "linear-gradient(135deg, rgba(59,130,246,1), rgba(99,102,241,1))",
  color: "#fff",
  fontWeight: 900,
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const softBtn = {
  width: 42,
  height: 42,
  borderRadius: 12,
  border: "1px solid rgba(20,30,60,.10)",
  background: "rgba(20,30,60,.04)",
  cursor: "pointer",
  display: "grid",
  placeItems: "center",
};

const dangerBtn = {
  width: 42,
  height: 42,
  borderRadius: 12,
  border: "1px solid rgba(220,38,38,.22)",
  background: "rgba(220,38,38,.08)",
  cursor: "pointer",
  display: "grid",
  placeItems: "center",
};

const rowCard = {
  display: "flex",
  justifyContent: "space-between",
  gap: 10,
  padding: 12,
  borderRadius: 14,
  border: "1px solid rgba(20,30,60,.10)",
  background: "rgba(255,255,255,.7)",
};

const topSaveBtn = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 14px",
  borderRadius: 12,
  border: "1px solid rgba(20,30,60,.10)",
  cursor: "pointer",
  background: "linear-gradient(135deg, rgba(59,130,246,1), rgba(99,102,241,1))",
  color: "#fff",
  fontWeight: 700,
};

const msgStyle = {
  marginBottom: 14,
  padding: "10px 12px",
  borderRadius: 12,
  border: "1px solid rgba(20,30,60,.10)",
  background: "rgba(59,130,246,.06)",
  color: "rgba(20,30,60,.9)",
  fontSize: 13,
};
